import React, { useState, useEffect, useCallback } from 'react';
import { workflowsApi } from '../services/api';
import { computeCounts } from '../utils/workflowTree';
import WorkflowStepList from './WorkflowStepList';

const fmtDuration = (min) => {
  const m = Number(min) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${r}` : `${h} h`;
};

/** Éditeur d'un modèle : champs inline + arbre d'étapes récursif. */
export default function WorkflowTemplateEditor({ templateId, onBack, onSaved }) {
  const [tpl, setTpl] = useState({
    name: '', category: '', category_color: '#2563eb', description: '', societe: '', steps: [], updated_at: null,
  });
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [categories, setCategories] = useState([]);
  const [societes, setSocietes] = useState([]);

  useEffect(() => {
    workflowsApi.getCategories().then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    workflowsApi.getSocietes().then(({ data }) => setSocietes(data.societes || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!templateId) { setLoading(false); return; }
    setLoading(true);
    workflowsApi.getTemplate(templateId)
      .then(({ data }) => {
        const t = data.template;
        setTpl({
          name: t.name || '', category: t.category || '', category_color: t.category_color || '#2563eb',
          description: t.description || '', societe: t.societe || '', steps: t.steps || [], updated_at: t.updated_at,
        });
      })
      .catch(() => alert('Modèle introuvable'))
      .finally(() => setLoading(false));
  }, [templateId]);

  const patch = useCallback((p) => { setTpl((prev) => ({ ...prev, ...p })); setDirty(true); }, []);
  const setSteps = useCallback((steps) => { setTpl((prev) => ({ ...prev, steps })); setDirty(true); }, []);

  const counts = computeCounts(tpl.steps);

  const save = async () => {
    if (!tpl.name.trim()) { alert('Le nom du modèle est obligatoire.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: tpl.name, category: tpl.category || null, category_color: tpl.category_color || null,
        description: tpl.description || null, societe: tpl.societe || null, steps: tpl.steps,
      };
      let res;
      if (templateId) res = await workflowsApi.updateTemplate(templateId, { ...payload, updated_at: tpl.updated_at });
      else res = await workflowsApi.createTemplate(payload);
      const saved = res.data.template;
      setTpl((prev) => ({ ...prev, updated_at: saved.updated_at }));
      setDirty(false);
      if (onSaved) onSaved(saved);
    } catch (e) {
      if (e?.response?.status === 409) alert(e.response.data.error);
      else alert('Échec de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="wf-loading">Chargement…</div>;

  return (
    <div className="wf-editor wf-flow">
      <div className="wf-editor-bar">
        <button className="wf-act-ghost" onClick={() => {
          if (dirty && !window.confirm('Modifications non enregistrées. Quitter ?')) return;
          onBack();
        }}>← Retour</button>
        <div className="wf-editor-summary">
          <span>{counts.step_count} étape{counts.step_count > 1 ? 's' : ''}</span>
          <span>{fmtDuration(counts.total_estimated_minutes)}</span>
        </div>
        {dirty
          ? <button className="wf-act primary" onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          : <span className="wf-saved">Enregistré ✓</span>}
      </div>

      <div className="wf-meta-block">
        <input
          className="wf-title-input"
          placeholder="Nom du modèle…"
          value={tpl.name}
          onChange={(e) => patch({ name: e.target.value })}
        />
        <div className="wf-meta-line">
          <label className="wf-mchip">
            <input type="color" className="wf-swatch" value={tpl.category_color || '#0891b2'} onChange={(e) => patch({ category_color: e.target.value })} title="Couleur de la catégorie" />
            <input list="wf-categories" value={tpl.category} placeholder="catégorie" onChange={(e) => patch({ category: e.target.value })} />
            <datalist id="wf-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </label>
          <label className="wf-mchip">
            🏢
            <input list="wf-societes" value={tpl.societe} placeholder="société" onChange={(e) => patch({ societe: e.target.value })} />
            <datalist id="wf-societes">{societes.map((s) => <option key={s} value={s} />)}</datalist>
          </label>
        </div>
        <textarea
          className="wf-desc-input"
          placeholder="Décrire le processus en une phrase…"
          rows={1}
          value={tpl.description}
          onChange={(e) => patch({ description: e.target.value })}
        />
      </div>

      <WorkflowStepList nodes={tpl.steps} onChange={setSteps} depth={0} />
    </div>
  );
}
